import * as React from 'react'

import { EDocumentComparisonChangeType, ETargetVersion } from '../../constants'
import { TDiffMarker } from '../../types'
import { IPDFCompareChangeItem } from '../types'

type LayoutMetrics = {
  sourceHeightRatio: number
  targetHeightRatio: number
  sourcePageCount: number
  targetPageCount: number
}

type UsePdfMinimapMarkersOptions = {
  compareDocumentDiff: IPDFCompareChangeItem[] | null
  sourceTotalPages?: number | null
  targetTotalPages?: number | null
  isEnabled: boolean
}

const MIN_MARKER_HEIGHT_PERCENT = 0.4
const SAFE_PAGE_COUNT_FALLBACK = 1

const getPageCount = (params: {
  providedTotal?: number | null
  changes: IPDFCompareChangeItem[]
  types: EDocumentComparisonChangeType[]
}) => {
  const { providedTotal, changes, types } = params
  if (providedTotal && providedTotal > 0) return providedTotal

  const maxFromChanges = changes
    .filter((change) => types.includes(change.type))
    .reduce((max, change) => {
      const pageIndex = change.pageIndex ?? SAFE_PAGE_COUNT_FALLBACK
      return Math.max(max, pageIndex)
    }, SAFE_PAGE_COUNT_FALLBACK)

  return Math.max(maxFromChanges, SAFE_PAGE_COUNT_FALLBACK)
}

const buildMarkersForVersions = (params: {
  changes: IPDFCompareChangeItem[]
  column: ETargetVersion
  types: EDocumentComparisonChangeType[]
  pageCount: number
}): TDiffMarker[] => {
  const { changes, column, types, pageCount } = params

  // Pre-compute a per-column max Y to normalize bbox positions if available.
  const maxYByPage = changes
    .filter((change) => types.includes(change.type))
    .reduce<Record<number, number>>((acc, change) => {
      const bbox =
        column === ETargetVersion.SOURCE ? change.sourceBbox : change.targetBbox

      if (!bbox) return acc

      const [, y0, , y1] = bbox
      const bottom = Math.max(y0, y1)
      const current = acc[change.pageIndex ?? 0] ?? 0
      acc[change.pageIndex ?? 0] = Math.max(current, bottom)
      return acc
    }, {})

  return changes
    .filter((change) => types.includes(change.type))
    .map((change, index) => {
      const pageIndex = change.pageIndex ?? SAFE_PAGE_COUNT_FALLBACK
      const clampedPage = Math.max(pageIndex, SAFE_PAGE_COUNT_FALLBACK)
      const pageSlotStart = (clampedPage - 1) / pageCount
      const pageSlotHeight = 1 / pageCount

      const bbox =
        column === ETargetVersion.SOURCE ? change.sourceBbox : change.targetBbox

      const pageMaxY = maxYByPage[change.pageIndex ?? 0] ?? 0
      const hasBbox = bbox && pageMaxY > 0

      const bboxTop = hasBbox ? Math.min(bbox[1], bbox[3]) : 0
      const bboxHeight = hasBbox ? Math.abs(bbox[3] - bbox[1]) : 0

      const withinPageTop = hasBbox ? bboxTop / pageMaxY : 0
      const withinPageHeight = hasBbox ? Math.max(bboxHeight / pageMaxY, 0) : 1

      const topPercent = (pageSlotStart + withinPageTop * pageSlotHeight) * 100
      const heightPercent = Math.max(
        withinPageHeight * pageSlotHeight * 100,
        MIN_MARKER_HEIGHT_PERCENT
      )

      return {
        key: change.uuid ?? `${column}-${index}`,
        column,
        topPercent,
        heightPercent,
        type: change.type,
      }
    })
}

export const usePdfMinimapMarkers = ({
  compareDocumentDiff,
  sourceTotalPages,
  targetTotalPages,
  isEnabled,
}: UsePdfMinimapMarkersOptions) => {
  const [diffMarkers, setDiffMarkers] = React.useState<{
    sourceMarkers: TDiffMarker[]
    targetMarkers: TDiffMarker[]
  } | null>(null)
  const [layoutMetrics, setLayoutMetrics] = React.useState<LayoutMetrics>({
    sourceHeightRatio: 100,
    targetHeightRatio: 100,
    sourcePageCount: SAFE_PAGE_COUNT_FALLBACK,
    targetPageCount: SAFE_PAGE_COUNT_FALLBACK,
  })

  const refreshMarkers = React.useCallback(() => {
    if (!isEnabled || !compareDocumentDiff?.length) {
      setDiffMarkers(null)
      setLayoutMetrics({
        sourceHeightRatio: 100,
        targetHeightRatio: 100,
        sourcePageCount: SAFE_PAGE_COUNT_FALLBACK,
        targetPageCount: SAFE_PAGE_COUNT_FALLBACK,
      })
      return
    }

    const sourcePageCount = getPageCount({
      providedTotal: sourceTotalPages,
      changes: compareDocumentDiff,
      types: [
        EDocumentComparisonChangeType.DELETE,
        EDocumentComparisonChangeType.REPLACE,
      ],
    })

    const targetPageCount = getPageCount({
      providedTotal: targetTotalPages,
      changes: compareDocumentDiff,
      types: [
        EDocumentComparisonChangeType.INSERT,
        EDocumentComparisonChangeType.REPLACE,
      ],
    })

    const maxPageCount = Math.max(
      sourcePageCount,
      targetPageCount,
      SAFE_PAGE_COUNT_FALLBACK
    )

    const nextLayoutMetrics: LayoutMetrics = {
      sourceHeightRatio: (sourcePageCount / maxPageCount) * 100,
      targetHeightRatio: (targetPageCount / maxPageCount) * 100,
      sourcePageCount,
      targetPageCount,
    }

    const sourceMarkers = buildMarkersForVersions({
      changes: compareDocumentDiff,
      column: ETargetVersion.SOURCE,
      types: [
        EDocumentComparisonChangeType.DELETE,
        EDocumentComparisonChangeType.REPLACE,
      ],
      pageCount: sourcePageCount,
    })

    const targetMarkers = buildMarkersForVersions({
      changes: compareDocumentDiff,
      column: ETargetVersion.TARGET,
      types: [
        EDocumentComparisonChangeType.INSERT,
        EDocumentComparisonChangeType.REPLACE,
      ],
      pageCount: targetPageCount,
    })

    setLayoutMetrics(nextLayoutMetrics)
    setDiffMarkers({ sourceMarkers, targetMarkers })
  }, [compareDocumentDiff, isEnabled, sourceTotalPages, targetTotalPages])

  React.useEffect(() => refreshMarkers(), [refreshMarkers])

  return React.useMemo(
    () => ({ diffMarkers, refreshMarkers, layoutMetrics }),
    [diffMarkers, refreshMarkers, layoutMetrics]
  )
}
